import { useMutation, useQueryClient } from '@tanstack/vue-query'

import type {
  StockSaveRequest,
  StockSaveResult
} from '@/domains/command-center/composables/use-stock-mode/useStockAdjustmentSave.types'
import type { StockShelf } from '@/domains/command-center/handlers/stock/stockMode.types'

import { stockOutcome } from '@/domains/adjustment-groups/utils/stock-outcome/stockOutcome'
import { STOCK_SEARCH_QUERY_KEY } from '@/domains/command-center/composables/use-stock-mode/useStockSearch'
import { shelfAt } from '@/domains/command-center/handlers/stock/stockVariants'
import { callCompositions, callInventoryItems } from '@/domains/inventory/services'
import { VARIANTS_QUERY_KEY } from '@/domains/products/composables/use-variants-query/useVariantsQuery'
import { availableQuantity } from '@/domains/products/utils/stock-level/stockLevel'

/**
 * The shelf the change is measured against.
 *
 * ⚠ **Set reads the shelf again.** The single adjust takes a change, never a total, so Set turns 48
 * into "+12 from 36" here. The 36 on screen is as old as the search, and a till sale since then would
 * leave 47. So Set asks once more, a moment before it writes. Add and Remove carry their own change,
 * so they need no fresh figure.
 */
const shelfToMeasure = async ({ variant, location, mode }: StockSaveRequest): Promise<StockShelf> => {
  if (mode !== 'set') return shelfAt(variant, location)

  const [fresh] = await callCompositions().variantsByIds([variant.id])

  // A read that names no variant is a failed read, not an empty shelf. Measured as empty, the save would try
  // to start a shelf that exists, and Set would write a change from zero.
  if (!fresh) throw new Error('Unable to read the shelf again. Try again.')

  return shelfAt({ ...variant, inventoryItems: fresh.inventory_items }, location)
}

/** The shelf's item, started at zero first when the variant is not stocked at the location yet. */
const itemUlidFor = async (shelf: StockShelf, { variant, location }: StockSaveRequest): Promise<string> => {
  if (shelf.item?.ulid) return shelf.item.ulid

  const created = await callInventoryItems().create({ variant_id: variant.id, location_id: location.id })

  return created.ulid
}

const saveStockAdjustment = async (request: StockSaveRequest): Promise<StockSaveResult> => {
  const shelf = await shelfToMeasure(request)
  const { delta } = stockOutcome({ current: shelf.current, mode: request.mode, quantity: request.quantity })

  // The fresh figure already matches the count, so nothing moves and nothing is written.
  if (delta === 0) return { before: shelf.current, after: shelf.current }

  const ulid = await itemUlidFor(shelf, request)
  const item = await callInventoryItems().adjust({
    inventory_item_ulid: ulid,
    adjusted_quantity: delta,
    reason: request.reason
  })
  const after = availableQuantity(item)

  return { before: after - delta, after }
}

/**
 * Saves one stock change from the palette, and answers the shelf before and after.
 *
 * The single adjust answers when the change is made, so the palette shows the real result at once.
 * Every list that shows stock is stale afterwards, so the palette's own search and the variant lists
 * are invalidated, the same keys a quick update invalidates.
 */
const useStockAdjustmentSave = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: saveStockAdjustment,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: STOCK_SEARCH_QUERY_KEY })
      void queryClient.invalidateQueries({ queryKey: VARIANTS_QUERY_KEY })
    }
  })
}

export { useStockAdjustmentSave }
